/**
 * NO localStorage reads or writes happen in this file.
 * All reads/writes use in-memory store with optional API write-through.
 */

import { communityConfigStore, isCommunityConfigHydrated } from "../store";
import { updateCommunityEntity } from "../../api/communityMetadata";

export interface CommunityOverride {
  description?: string;
  visibility?: "private" | "public";
  defaultProjectVisibility?: "draft" | "community" | "public";
  primaryLocationGuid?: string;

  // NEW (Phase 3): Draft project visibility policy for the community
  draftVisibility?: "admins_only" | "members";

  // Hero image (matches project hero_image_url pattern)
  hero_image_url?: string;
}

function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

function readAll(): Record<string, CommunityOverride> {
  ensureWarm();
  const result: Record<string, CommunityOverride> = {};
  communityConfigStore.forEach((val, key) => {
    result[key] = val;
  });
  return result;
}

function writeAll(data: Record<string, CommunityOverride>) {
  ensureWarm();
  communityConfigStore.clear();
  for (const [key, val] of Object.entries(data)) {
    communityConfigStore.set(key.toUpperCase(), val);
  }
  try {
    window.dispatchEvent(new CustomEvent("l4e_community_config_changed"));
  } catch {
    // ignore
  }
}

export function getCommunityOverride(guid: string): CommunityOverride {
  const all = readAll();
  const gUp = String(guid || "").trim().toUpperCase();
  // Try exact match first
  if (all[gUp]) return all[gUp];
  // Fallback to original casing for backward compatibility
  return all[guid] || {};
}

export function setCommunityOverride(guid: string, data: CommunityOverride) {
  const gUp = String(guid || "").trim().toUpperCase();
  const all = readAll();
  all[gUp] = { ...(all[gUp] || {}), ...(data || {}) };
  writeAll(all);

  // API write-through for hero_image_url
  if (data.hero_image_url !== undefined) {
    updateCommunityEntity(guid, { heroImageUrl: data.hero_image_url }).catch(
      (err) => {
        console.warn("[communityConfigOverrides] API write failed:", err);
      }
    );
  }
}
